import { useState } from 'react';
import { Code, Copy, Check, Terminal, Zap, Shield, Globe, ChevronRight, FileText, Key, Webhook } from 'lucide-react';
import './ApiPage.css';

const ApiPage = () => {
  const [copiedCode, setCopiedCode] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const codeExamples = {
    curl: `curl -X POST https://api.omnibridge.com/v1/exchange/estimate \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "fromCurrency": "btc",
    "toCurrency": "eth",
    "fromAmount": 0.5
  }'`,
    javascript: `const response = await fetch('https://api.omnibridge.com/v1/exchange/estimate', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    fromCurrency: 'btc',
    toCurrency: 'eth',
    fromAmount: 0.5
  })
});

const data = await response.json();
console.log(data.estimatedAmount);`,
    python: `import requests

response = requests.post(
    'https://api.omnibridge.com/v1/exchange/estimate',
    headers={
        'Authorization': 'Bearer YOUR_API_KEY',
        'Content-Type': 'application/json'
    },
    json={
        'fromCurrency': 'btc',
        'toCurrency': 'eth',
        'fromAmount': 0.5
    }
)

data = response.json()
print(data['estimatedAmount'])`,
  };

  const features = [
    { icon: Zap, title: 'Lightning Fast', desc: 'Sub-100ms API response times globally' },
    { icon: Shield, title: 'Bank-Grade Security', desc: 'Enterprise-level encryption & security' },
    { icon: Globe, title: 'Global Infrastructure', desc: '99.99% uptime with redundant servers' },
    { icon: Webhook, title: 'Webhooks', desc: 'Real-time notifications for transactions' },
  ];

  const endpoints = [
    { method: 'GET', path: '/v1/currencies', desc: 'Get all supported currencies', auth: false },
    { method: 'POST', path: '/v1/exchange/estimate', desc: 'Get exchange rate estimate', auth: true },
    { method: 'POST', path: '/v1/exchange/create', desc: 'Create a new exchange', auth: true },
    { method: 'GET', path: '/v1/exchange/:id', desc: 'Get exchange status', auth: true },
    { method: 'POST', path: '/v1/validate-address', desc: 'Validate wallet address', auth: false },
  ];

  const copyCode = (lang) => {
    navigator.clipboard.writeText(codeExamples[lang]);
    setCopiedCode(lang);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="api-page">
      {/* Background Animation */}
      <div className="api-bg">
        <div className="code-rain">
          {Array.from({ length: 20 }).map((_, i) => (
            <span key={i} className="code-char" style={{ 
              left: `${i * 5}%`, 
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}>
              {String.fromCharCode(0x30 + Math.floor(Math.random() * 16))}
            </span>
          ))}
        </div>
        <div className="api-glow"></div>
      </div>

      <div className="api-container">
        {/* Hero Section */}
        <section className="api-hero">
          <div className="hero-badge">
            <Code size={18} />
            <span>Developer API</span>
          </div>
          <h1 className="api-title">
            Build with <span className="gradient-text">Omini Bridge</span>
          </h1>
          <p className="api-subtitle">
            Powerful REST API for crypto exchanges. Integrate seamless crypto swaps into your 
            application with just a few lines of code.
          </p>
          <div className="hero-cta">
            <button className="cta-primary">
              <Key size={18} />
              Get API Key
            </button>
            <button className="cta-secondary">
              <FileText size={18} />
              View Docs
            </button>
          </div>
        </section>

        {/* Features */}
        <section className="api-features">
          {features.map((feature, index) => (
            <div key={index} className="api-feature-card" style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="feature-icon-wrapper">
                <feature.icon size={28} />
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
            </div>
          ))}
        </section>

        {/* Code Example Section */}
        <section className="code-section">
          <div className="code-header">
            <h2>Try it Now</h2>
            <p>Get exchange rates with a simple API call</p>
          </div>
          
          <div className="code-editor">
            <div className="editor-tabs">
              {Object.keys(codeExamples).map((lang) => (
                <button
                  key={lang}
                  className={`editor-tab ${activeTab === lang ? 'active' : ''}`}
                  onClick={() => setActiveTab(lang)}
                >
                  {lang.charAt(0).toUpperCase() + lang.slice(1)}
                </button>
              ))}
            </div>
            
            <div className="editor-body">
              <div className="editor-toolbar">
                <div className="window-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <button 
                  className="copy-btn"
                  onClick={() => copyCode(activeTab)}
                >
                  {copiedCode === activeTab ? <Check size={16} /> : <Copy size={16} />}
                  {copiedCode === activeTab ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="code-content">
                <code>{codeExamples[activeTab]}</code>
              </pre>
            </div>
          </div>
        </section>

        {/* Endpoints Section */}
        <section className="endpoints-section">
          <div className="section-header">
            <h2>API Endpoints</h2>
            <p>Comprehensive endpoints for all your crypto exchange needs</p>
          </div>
          
          <div className="endpoints-list">
            {endpoints.map((endpoint, index) => (
              <div key={index} className="endpoint-card">
                <div className="endpoint-method" data-method={endpoint.method}>
                  {endpoint.method}
                </div>
                <div className="endpoint-info">
                  <code className="endpoint-path">{endpoint.path}</code>
                  <p className="endpoint-desc">{endpoint.desc}</p>
                </div>
                <div className="endpoint-meta">
                  {endpoint.auth && <span className="auth-badge">Auth Required</span>}
                  <ChevronRight size={20} className="endpoint-arrow" />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Stats Section */}
        <section className="api-stats">
          <div className="stat-item">
            <span className="stat-number">10M+</span>
            <span className="stat-label">API Calls/Day</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">50+</span>
            <span className="stat-label">Countries</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">99.99%</span>
            <span className="stat-label">Uptime</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">&lt;100ms</span>
            <span className="stat-label">Response Time</span>
          </div>
        </section>

        {/* CTA Section */}
        <section className="api-cta-section">
          <div className="cta-content">
            <h2>Ready to start building?</h2>
            <p>Get your API keys and start integrating in minutes</p>
            <button className="cta-large">
              <Terminal size={20} />
              Start Building
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ApiPage;
